import invert from 'lodash/invert';
import pick from 'lodash/pick';

import {Segmentation} from 'common/widgets/property-filter-menu/models/segfilter';
import {Segment} from 'common/widgets/query-block/segment-menu/types';
import {CohortsClause, GroupClause, QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from 'common/report/models/insights/clause';
import {DATASET_MIXPANEL} from 'common/report/constants';
import {CohortsSection, GroupSection} from 'common/report/models/insights/section';
import {InsightsReportSectionsOverride} from 'common/report/models/insights/types';

/**
 * Transforms Cohorts and Group Sections into Segments
 */
// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getSelectedSegments(cohortClauses, groupClauses): Segment[][] {
  const convertGroupClause = (clause) => {
    if (clause instanceof CohortsClause) {
      return {cohort: pick(clause, [`id`, `name`, `negated`, `raw_cohort`, `data_group_id`])};
    } else {
      const resourceType = invert(QUERY_BUILDER_RESOURCE_TYPE_MAPPING)[clause.resourceType];

      return {
        [resourceType]: {
          resourceType,
          propertyDefaultType: clause.propertyType,
          propertyName: clause.value,
          propertyType: clause.typeCast || clause.propertyType,
          ...(clause.propertyObjectKey ? {propertyObjectKey: clause.propertyObjectKey} : {}),
          ...(clause.customPropertyId ? {customPropertyId: clause.customPropertyId} : null),
          ...(clause.customProperty ? {customProperty: clause.customProperty} : null),
          ...(clause.dataGroupId ? {dataGroupId: clause.dataGroupId} : {}),
          ...(clause.unit ? {unit: clause.unit} : {}),
          ...(clause.customBucket ? {customBucket: clause.customBucket} : {}),
          ...(clause.joinPropertyType ? {joinPropertyType: clause.joinPropertyType} : {}),
          ...(clause.tempDataGroupId ? {tempDataGroupId: clause.tempDataGroupId} : {}),
        },
      };
    }
  };

  return [
    ...(cohortClauses.length ? [[cohortClauses.map((clause) => convertGroupClause(clause))]] : []),
    ...groupClauses.map((clause) => [convertGroupClause(clause)]),
  ];
}

/**
 * Transforms Segments into Cohorts and Group Sections
 */
export function getSegmentSections(segments: Segment[]): [CohortsSection, GroupSection] {
  let cohortsClauses = [];
  const groupClauses = [];

  segments.forEach((segment) => {
    // TODO: insights currently only supports a single segment of cohorts values
    if (Array.isArray(segment) && segment.length) {
      cohortsClauses = segment.map((item) => new CohortsClause({...item.cohort, dataset: DATASET_MIXPANEL}));
    } else {
      const groupBy: Segmentation = Object.values(segment)[0];

      groupClauses.push(
        new GroupClause({
          ...(groupBy.customPropertyId ? {customPropertyId: groupBy.customPropertyId} : null),
          ...(groupBy.customProperty ? {customProperty: groupBy.customProperty} : null),
          dataset: DATASET_MIXPANEL,
          dataGroupId: groupBy.dataGroupId,
          ...(groupBy.joinPropertyType ? {joinPropertyType: groupBy.joinPropertyType} : {}),
          ...(groupBy.tempDataGroupId ? {tempDataGroupId: groupBy.tempDataGroupId} : {}),
          propertyType: groupBy.propertyDefaultType,
          ...(groupBy.propertyObjectKey ? {propertyObjectKey: groupBy.propertyObjectKey} : {}),
          ...(groupBy.propertyType !== groupBy.propertyDefaultType ? {typeCast: groupBy.propertyType} : {}),
          resourceType: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[groupBy.resourceType],
          value: groupBy.propertyName,
          ...(groupBy.unit
            ? {
                unit: groupBy.unit,
              }
            : {}),
          ...(groupBy.customBucket ? {customBucket: groupBy.customBucket} : null),
        }),
      );
    }
  });

  return [new CohortsSection(cohortsClauses), new GroupSection(groupClauses)];
}

export function getSegmentSectionsOverride(segments: Segment[]): InsightsReportSectionsOverride {
  const [cohortsSection, groupSection] = getSegmentSections(segments);
  return {
    cohorts: cohortsSection.toBookmarkData(),
    group: groupSection.toBookmarkData(),
  };
}
